import { Box, Button, Text, useToast, VStack } from "@chakra-ui/react";
import { ApiMessageStack, Error, Loader, TableV2 } from "components";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { useActionPrompt } from "utils/react-hooks/useActionPrompt.hook";
import { TLeadIconConfig } from "types/lead-icon.type";
import { TSelectOption } from "types/select-input.type";
import { getLeadSourceLabel } from "utils/lead-source.utils";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { leadIconsColumnDef } from "./leadIconsColumnDef";
import { CreateLeadIconActionPrompt } from "./ActionPrompts/CreateLeadIconActionPrompt";
import { EditLeadIconActionPrompt } from "./ActionPrompts/EditLeadIconActionPrompt";
import { ConfirmActionPrompt } from "routes/dashboard/components/prompts/ConfirmActionPrompt";
import { getLeadTypeLabel } from "utils/lead-type.utils";

const MANUALLY_ADDED_SOURCE_OPTION: TSelectOption<string> = {
  value: "manually_added",
  label: "Manually Added",
};

interface IProps {
  accountStore: UserAccountStore;
}

export const LeadIconsConfig: FC<IProps> = observer(({ accountStore }) => {
  const accountLeadIconsStore = accountStore.accountLeadIconsStore;
  const accountLeadSourcesStore = accountStore.accountLeadSourcesStore;
  const toast = useToast();
  const [loadingStatus, setLoadingStatus] = useState<ApiRequestStatusEnum>(
    ApiRequestStatusEnum.NONE
  );
  const { setModal, unSetModal } = useActionPrompt();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingStatus(ApiRequestStatusEnum.PENDING);
        await Promise.all([
          accountLeadIconsStore.fetchAccountLeadIcons(),
          accountLeadSourcesStore.fetchAccountLeadSources(),
          accountLeadSourcesStore.fetchCurrentlyActiveAccountLeadSources(),
        ]);
        setLoadingStatus(ApiRequestStatusEnum.SUCCESS);
      } catch (error) {
        setLoadingStatus(ApiRequestStatusEnum.ERROR);
      }
    };

    fetchData();
  }, [accountLeadIconsStore, accountLeadSourcesStore]);

  const sourceOptions = useMemo<TSelectOption<string>[]>(() => {
    const accountSourceOptions = accountLeadSourcesStore.currentlyActiveLeadSourcesArray.map(
      leadSource => ({
        value: leadSource.source,
        label: getLeadSourceLabel(leadSource.source, leadSource),
      })
    );
    return [MANUALLY_ADDED_SOURCE_OPTION, ...accountSourceOptions];
  }, [accountLeadSourcesStore.currentlyActiveLeadSourcesArray]);

  const getSourceLabel = useCallback(
    (source: string) =>
      getLeadSourceLabel(
        source,
        accountLeadSourcesStore.leadSourcesMapBySource[source]
      ),
    [accountLeadSourcesStore.leadSourcesMapBySource]
  );

  const onDeleteLeadIcon = useCallback(
    (leadIconId: number) => async () => {
      try {
        await accountLeadIconsStore.deleteLeadIcon(leadIconId);
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={"Lead icon removed."} />,
        });
      } catch (e) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={e.message} />,
        });
      }
    },
    [accountLeadIconsStore, toast]
  );

  const handleRemove = useCallback(
    (row: TLeadIconConfig) => () => {
      setModal(
        <ConfirmActionPrompt
          text={`Please confirm that you want to remove the ${getSourceLabel(
            row.source
          )} / ${getLeadTypeLabel(row.type)} lead icon.`}
          onConfirm={onDeleteLeadIcon(row.id)}
          closePrompt={unSetModal}
        />
      );
    },
    [onDeleteLeadIcon, setModal, unSetModal, getSourceLabel]
  );

  const handleEdit = useCallback(
    (row: TLeadIconConfig) => () => {
      setModal(
        <EditLeadIconActionPrompt
          closePrompt={unSetModal}
          leadIcon={row}
          sourceLabel={getSourceLabel(row.source)}
          updateLeadIcon={accountLeadIconsStore.updateLeadIcon}
        />
      );
    },
    [setModal, unSetModal, getSourceLabel, accountLeadIconsStore]
  );

  const handleAdd = useCallback(() => {
    setModal(
      <CreateLeadIconActionPrompt
        closePrompt={unSetModal}
        sourceOptions={sourceOptions}
        createLeadIcon={accountLeadIconsStore.createLeadIcon}
      />
    );
  }, [setModal, unSetModal, sourceOptions, accountLeadIconsStore]);

  return (
    <VStack spacing={8} align={"stretch"}>
      <Text fontSize={"2xl"} fontWeight={"bold"} mb={-4}>
        Lead icons
      </Text>
      <Text fontSize={"sm"} color={"leadpro.500"}>
        Configure a custom icon to display against leads of a chosen source and
        type.
      </Text>
      {loadingStatus === ApiRequestStatusEnum.PENDING && <Loader />}
      {loadingStatus === ApiRequestStatusEnum.ERROR && <Error />}
      {loadingStatus === ApiRequestStatusEnum.SUCCESS && (
        <TableV2<TLeadIconConfig>
          striped
          dataSource={accountLeadIconsStore.leadIconConfigsArray}
          columns={leadIconsColumnDef(getSourceLabel, handleEdit, handleRemove)}
          additionalActions={
            <Box flexGrow={1}>
              <Button variant={"link"} colorScheme={"blue"} onClick={handleAdd}>
                + Add lead icon
              </Button>
            </Box>
          }
        />
      )}
    </VStack>
  );
});
